import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Book, Code, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchaseSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchaseData, setPurchaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyPurchase = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${API}/purchase/verify/${sessionId}`);
      setPurchaseData(response.data);
    } catch (error) {
      console.error('Verification error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPurchase(sessionId);
    } else {
      setLoading(false);
    }
  }, [searchParams, verifyPurchase]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-5xl font-bold mb-4">
            Purchase <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600">Successful!</span>
          </h1>
          <p className="text-xl text-gray-400">
            Thank you for purchasing CryptoPulse Pro
          </p>
        </motion.div>

        {purchaseData?.success && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Access Details */}
            <Card className="p-8 bg-white/5 border-white/10">
              <h2 className="text-2xl font-bold mb-6">Your Access Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400">License Type</label>
                  <p className="text-lg font-semibold capitalize">{purchaseData.license_type}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Access Code</label>
                  <div className="flex items-center gap-3 mt-1">
                    <code className="flex-1 bg-black/50 px-4 py-3 rounded-lg font-mono text-emerald-400">
                      {purchaseData.access_code}
                    </code>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(purchaseData.access_code);
                      }}
                      variant="outline"
                      className="border-white/20"
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Save this code - you'll need it for support requests</p>
                </div>
              </div>
            </Card>

            {/* Quick Start */}
            <Card className="p-8 bg-gradient-to-br from-emerald-500/10 to-purple-600/10 border-emerald-500/20">
              <h2 className="text-2xl font-bold mb-6">Get Started in 3 Steps</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">1. Download Source Code</h3>
                    <p className="text-gray-400 mb-3">Access the complete platform codebase</p>
                    <Button
                      onClick={() => window.open(purchaseData.download_url, '_blank')}
                      className="bg-white/10 hover:bg-white/20"
                    >
                      <Code className="w-4 h-4 mr-2" />
                      Access GitHub Repository
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                    <Book className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">2. Read Documentation</h3>
                    <p className="text-gray-400 mb-3">Complete setup and deployment guides</p>
                    <Button
                      onClick={() => window.open(purchaseData.documentation_url, '_blank')}
                      className="bg-white/10 hover:bg-white/20"
                    >
                      <Book className="w-4 h-4 mr-2" />
                      View Documentation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">3. Deploy & Launch</h3>
                    <p className="text-gray-400">Follow our one-click deployment guide</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Support */}
            <Card className="p-6 bg-white/5 border-white/10">
              <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
              <p className="text-gray-400 mb-4">
                Our support team is here to help you get started. Email us at support@cryptopulsepro.com with your access code.
              </p>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-white/20"
              >
                Return to Homepage
              </Button>
            </Card>
          </motion.div>
        )}

        {!purchaseData?.success && (
          <Card className="p-8 bg-white/5 border-white/10 text-center">
            <p className="text-gray-400 mb-4">Unable to verify purchase. Please check your email for access details.</p>
            <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-emerald-500 to-purple-600">
              Return to Homepage
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PurchaseSuccess;